import { Field } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';

import { CheckBoxGroup, RadioButtonGroup } from '../Fields/FieldGroupings';
import { CheckBox, RadioButton, SelectOptions } from '../Fields/Fields';
import * as styles from './InputTypeSwitch.module.css';
import { validate } from './validations';

/**
 * Legacy validation
 * @param message
 * @returns {*}
 * @constructor
 */
const MarketoValidation = ({ message }) => (
  <div className="mktoError" style={{ right: '169px', bottom: '-19px' }}>
    <div className="mktoErrorArrowWrap">
      <div className="mktoErrorArrow" />
    </div>
    <div className="mktoErrorMsg">{message}</div>
  </div>
);

const ValidationError = ({ message }) => {
  return (
    <div className={styles.errorMessage}>
      <span>{message}</span>
    </div>
  );
};

const InputTypeSwitch = ({
  fieldData,
  name,
  formValues,
  formSetFieldValue,
  isOneColumn,
  error = {},
  marketoValidation,
  hasOtherField = false,
}) => {
  const testValid = (value) =>
    validate(fieldData.dataType, fieldData.required, fieldData.validationMessage, name, value);
  let field;
  const controlStyle = error[name] ? styles.formControlError : styles.formControl;
  const validation = marketoValidation ? (
    <MarketoValidation message={error[name]} />
  ) : (
    <ValidationError message={error[name]} />
  );

  let label = fieldData.label;
  if (fieldData?.label?.indexOf('*') === -1 && fieldData?.required) {
    label = `${fieldData.label} *`;
  }

  switch (fieldData.type.toLowerCase()) {
    case 'htmltext': {
      field = (
        <div className={styles.formField}>
          <div className={styles.label} dangerouslySetInnerHTML={{ __html: fieldData.text }} />
        </div>
      );
      break;
    }
    case 'select': {
      const options = fieldData.type === 'select' ? <SelectOptions values={fieldData.fieldMetaData.values} /> : null;

      if (fieldData.fieldMetaData.multiSelect) {
        const onChange = (evt) => {
          formSetFieldValue(
            name,
            [].slice.call(evt.target.selectedOptions).map((option) => option.value)
          );
        };
        field = (
          <div className={`${styles.formField}`}>
            <label className={styles.label}>{label}</label>
            <Field
              component={fieldData.type}
              className={`${controlStyle} ${styles.formField}`}
              type={fieldData.dataType}
              name={name}
              id={name}
              multiple={true}
              onChange={onChange}
              required={fieldData.required}
              value={formValues[fieldData.name] || []}
              validate={testValid}
            >
              {options}
            </Field>
            {error[name] && validation}
          </div>
        );
      } else {
        field = (
          <div className={`${styles.formField}`}>
            <label className={styles.label}>{label}</label>
            <Field
              component={fieldData.type}
              className={`${controlStyle} ${styles.formField}`}
              type={fieldData.dataType}
              name={name}
              id={name}
              required={fieldData.required}
              validate={testValid}
            >
              {options}
            </Field>
            {error[name] && validation}
          </div>
        );
      }
      break;
    }
    case 'checkbox': {
      field = (
        <Field
          component={CheckBox}
          name={fieldData.name}
          label={label}
          className={styles.formField}
          required={fieldData.required}
          validate={testValid}
        />
      );
      break;
    }
    case 'checkboxes': {
      const checkboxes = fieldData.fieldMetaData.values.map((v) => {
        return <Field key={v.value} component={CheckBox} name={fieldData.name} id={v.value} label={v.label} />;
      });
      field = (
        <>
          <CheckBoxGroup
            id={fieldData.name}
            label={label}
            value={formValues[fieldData.name]}
            onChange={formSetFieldValue}
            classes={styles.formField}
            required={fieldData.required}
            validate={testValid}
          >
            {checkboxes}
          </CheckBoxGroup>
          {error[name] && validation}
        </>
      );
      break;
    }
    case 'radiobuttons': {
      const radios = fieldData.fieldMetaData.values.map((v) => {
        return <Field key={v.value} component={RadioButton} name={fieldData.name} id={v.value} label={v.label} />;
      });

      field = (
        <>
          <RadioButtonGroup
            id={fieldData.name}
            label={label}
            /*value={formValues[fieldData.name]}*/
            className={[styles.formField, styles.radioGroup].join(' ')}
            required={fieldData.required}
            validate={testValid}
          >
            {radios}
          </RadioButtonGroup>
          {error[name] && validation}
        </>
      );
      break;
    }

    case 'textarea': {
      /**
        Optionally display the "Other" field based on another field
      **/
      const hasOtherSelected = formValues['whereDidYouHearAboutUs'] === 'Other';

      if (name !== 'whereDidYouHearAboutUsOther' || (name === 'whereDidYouHearAboutUsOther' && hasOtherSelected)) {
        field = (
          <div className={[styles.formField, isOneColumn ? styles.oneCol : ''].join(' ')}>
            <label className={styles.label}>{label}</label>
            <Field
              component={fieldData.type.toLowerCase()}
              className={`${controlStyle}`}
              type={fieldData.dataType}
              placeholder={fieldData.hintText}
              name={name}
              id={name}
              rows={2}
              required={fieldData.required}
              validate={testValid}
            />
            {error[name] && validation}
          </div>
        );
      } else {
        field = null;
      }
      break;
    }

    default: {
      field = (
        <div className={`${styles.formField} ${isOneColumn && styles.oneCol}`}>
          <label className={styles.label}>{label}</label>
          <Field
            component={fieldData.type.toLowerCase()}
            className={`${controlStyle}`}
            type={fieldData.dataType}
            placeholder={fieldData.hintText}
            name={name}
            id={name}
            required={fieldData.required}
            validate={testValid}
          />
          {error[name] && validation}
        </div>
      );
      break;
    }
  }
  return <>{field}</>;
};

MarketoValidation.propTypes = { message: PropTypes.string.isRequired };
ValidationError.propTypes = { message: PropTypes.string };

InputTypeSwitch.propTypes = {
  /* Passed through from the Formik component's setFieldValue prop */
  formSetFieldValue: PropTypes.func.isRequired,
  /* Formik's values prop passed through */
  formValues: PropTypes.any,
  /** Field information provided by Marketo */
  fieldData: PropTypes.shape({
    type: PropTypes.string,
    defaultValue: PropTypes.any,
    order: PropTypes.number,
    id: PropTypes.any.isRequired,
    name: PropTypes.string,
    label: PropTypes.string,
    isRequired: PropTypes.bool,
    maxLength: PropTypes.number,
    validationMessage: PropTypes.string,
    dataType: PropTypes.string,
    text: PropTypes.string,
    fieldMetaData: PropTypes.object,
    required: PropTypes.bool,
    hintText: PropTypes.string,
  }),
  /** Form field name */
  name: PropTypes.string,
  /** Bool if the field has a pairing and only spans 1 grid column */
  isOneColumn: PropTypes.bool,
  /** Validation error message */
  error: PropTypes.object,
  /** Bool to use the legacy Marketo style validation */
  marketoValidation: PropTypes.bool,
};

export default InputTypeSwitch;
